import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { BottomNav } from "./components/ui";
import type { ScreenId } from "./components/ui";
import { Lightbox } from "./components/Lightbox";
import type { LightboxTile } from "./components/Lightbox";
import { HomeScreen } from "./screens/Home";
import { UploadScreen } from "./screens/Upload";
import { FindMeScreen } from "./screens/FindMe";
import { MomentsScreen } from "./screens/Moments";
import type { MomentsLayout } from "./screens/Moments";
import { MomentDetail } from "./screens/MomentDetail";
import { SearchScreen } from "./screens/Search";
import { AllPhotos } from "./screens/AllPhotos";
import type { FloralDensity } from "./components/Florals";
import { useBranding, useKeepStatus, useKeepTheme, useSettings } from "./lib/keep";
import { StatusScreen } from "./screens/StatusScreen";

interface LightboxState {
  tiles: LightboxTile[];
  index: number;
}

// URL path <-> screen. Deep links like /upload load straight into that screen; the
// "moment" detail has no stable URL of its own, so it maps back to /moments.
const SCREEN_PATHS: Record<Exclude<ScreenId, "moment">, string> = {
  home: "/",
  upload: "/upload",
  findme: "/findme",
  moments: "/moments",
  search: "/search",
  all: "/all",
};

function screenFromPath(pathname: string): ScreenId {
  const clean = pathname.replace(/\/+$/, "") || "/";
  const entry = (Object.entries(SCREEN_PATHS) as [ScreenId, string][]).find(([, p]) => p === clean);
  return entry ? entry[0] : "home";
}

function pathForScreen(screen: ScreenId): string {
  return screen === "moment" ? SCREEN_PATHS.moments : SCREEN_PATHS[screen];
}

// Push the screen's path while preserving the query string (e.g. ?event=<slug>).
function syncUrl(screen: ScreenId) {
  const url = pathForScreen(screen) + window.location.search;
  if (url !== window.location.pathname + window.location.search) {
    window.history.pushState(null, "", url);
  }
}

export default function App() {
  // Per-event theme + layout, resolved from the API (demo defaults in mock mode).
  const branding = useBranding();
  const settings = useSettings();
  const keepTheme = useKeepTheme();
  const status = useKeepStatus();
  const THEME = {
    ...branding.theme,
    density: branding.theme.density as FloralDensity,
    momentsLayout: branding.theme.momentsLayout as MomentsLayout,
  };
  const [screen, setScreen] = useState<ScreenId>(() => screenFromPath(window.location.pathname));
  const [moment, setMoment] = useState<string | null>(null);
  const [lb, setLb] = useState<LightboxState | null>(null);
  const [navKey, setNavKey] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const go = (s: ScreenId) => {
    setMoment(null);
    setScreen(s);
    setNavKey((k) => k + 1);
    syncUrl(s);
  };
  const openMoment = (id: string) => {
    setMoment(id);
    setScreen("moment");
    setNavKey((k) => k + 1);
    syncUrl("moment");
  };
  const openLightbox = (tiles: LightboxTile[], index: number) => setLb({ tiles, index });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [navKey]);

  // Keep screen in sync with browser back/forward navigation.
  useEffect(() => {
    const onPop = () => {
      setMoment(null);
      setScreen(screenFromPath(window.location.pathname));
      setNavKey((k) => k + 1);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // scriptFont may be a bare family name ("Pinyon Script") or a full stack ('"Newsreader", serif').
  const scriptFamily = /["',]/.test(THEME.scriptFont) ? THEME.scriptFont : `"${THEME.scriptFont}", cursive`;
  const rootVars = {
    "--c-ink": THEME.ink,
    "--c-ink-soft": THEME.inkSoft,
    "--c-muted": THEME.muted,
    "--c-line": THEME.line,
    "--c-accent": THEME.accent,
    "--c-accent-deep": THEME.accentDeep,
    "--c-accent-soft": THEME.accentSoft,
    "--c-page": THEME.page,
    "--c-paper": THEME.paper,
    "--c-card": THEME.card,
    "--font-script": scriptFamily,
  } as CSSProperties;

  const recent = useMemo(() => [612, 488, 333, 720, 901, 145, 560, 277], []);

  // Face matching and guest uploads can each be disabled per-event; treat a blocked
  // /findme or /upload as Home.
  const faceMatch = settings.faceMatch;
  const canUpload = settings.acceptingUploads !== false;
  let body: React.ReactNode = null;
  if (screen === "home" || (screen === "findme" && !faceMatch) || (screen === "upload" && !canUpload))
    body = <HomeScreen go={go} totalPhotos={1248} density={THEME.density} recent={recent} openLightbox={openLightbox} />;
  else if (screen === "all") body = <AllPhotos go={go} density={THEME.density} openLightbox={openLightbox} />;
  else if (screen === "upload") body = <UploadScreen go={go} density={THEME.density} />;
  else if (screen === "findme") body = <FindMeScreen go={go} density={THEME.density} />;
  else if (screen === "moments")
    body = <MomentsScreen go={go} openMoment={openMoment} layout={THEME.momentsLayout} density={THEME.density} />;
  else if (screen === "moment")
    body = <MomentDetail moment={moment} go={go} density={THEME.density} openLightbox={openLightbox} />;
  else if (screen === "search")
    body = <SearchScreen go={go} openMoment={openMoment} density={THEME.density} openLightbox={openLightbox} />;

  const navCurrent: ScreenId = screen === "moment" ? "moments" : screen === "all" ? "home" : screen;

  // Not a public, published gallery → themed private / not-found / loading page (no nav).
  if (status !== "ready") {
    return (
      <div className={"app" + (keepTheme.dark ? " theme-dark" : "")} id="wp-app" style={rootVars}>
        <div className="paper-noise" />
        <StatusScreen />
      </div>
    );
  }

  return (
    <div className={"app" + (keepTheme.dark ? " theme-dark" : "")} id="wp-app" style={rootVars}>
      <div className="paper-noise" />
      <div className="scroll" ref={scrollRef}>
        <div className="screen-anim" key={navKey}>
          {body}
        </div>
        <div style={{ height: 96 }} />
      </div>
      <BottomNav current={navCurrent} go={go} onUpload={() => go("upload")} showFindMe={faceMatch} showUpload={canUpload} />

      {lb && <Lightbox tiles={lb.tiles} index={lb.index} onClose={() => setLb(null)} />}
    </div>
  );
}
